import type { DiagramEndpoint, DiagramNode, LessonDay } from './types';

const endpoint = (
  id: string,
  method: DiagramEndpoint['method'],
  path: string,
  purpose: string,
  request: string,
  response: string,
): DiagramEndpoint => ({ id, method, path, purpose, request, response });

const genericEndpoints = (title: string): DiagramEndpoint[] => [
  endpoint('read', 'GET', '/v1/resources/:id', `Read one ${title.toLowerCase()} resource`, 'path: { id: "r_42" }', '200 { "id": "r_42", "version": 7 }'),
  endpoint('write', 'POST', '/v1/resources', `Create a ${title.toLowerCase()} resource`, '{ "payload": { "name": "example" } }', '201 { "id": "r_43", "status": "created" }'),
];

const endpointCatalog: Record<string, DiagramEndpoint[]> = {
  'Scalability Fundamentals': [
    endpoint('read', 'GET', '/v1/items/:id', 'Serve a read-heavy user request', 'path: { id: "item_42" }', '200 { "id": "item_42", "name": "Interview Kit" }'),
    endpoint('write', 'POST', '/v1/items', 'Create data through the write path', '{ "name": "Interview Kit" }', '201 { "id": "item_42", "version": 1 }'),
    endpoint('health', 'GET', '/healthz', 'Let the load balancer remove unhealthy instances', 'no request body', '200 { "status": "healthy" }'),
  ],
  'Requirements & Estimation': [
    endpoint('read', 'GET', '/v1/resources/:id', 'Estimate the dominant read path', 'path: { id: "r_42" }', '200 { "id": "r_42", "data": "..." }'),
    endpoint('write', 'POST', '/v1/resources', 'Estimate payload size and write QPS', '{ "data": "...", "idempotencyKey": "req_9" }', '202 { "id": "r_43", "status": "queued" }'),
  ],
  'Load Balancing': [
    endpoint('checkout', 'POST', '/v1/checkout', 'Route a state-changing request safely', '{ "cartId": "c_19", "idempotencyKey": "pay_88" }', '202 { "orderId": "o_71", "status": "processing" }'),
    endpoint('health', 'GET', '/healthz', 'Continuously probe every application instance', 'no request body', '200 { "status": "healthy", "load": 0.42 }'),
  ],
  'Caching': [
    endpoint('read', 'GET', '/v1/products/:id', 'Show cache-aside on the hot read path', 'path: { id: "p_42" }', '200 { "id": "p_42", "price": 1499, "cache": "hit" }'),
    endpoint('update', 'PATCH', '/v1/products/:id', 'Update source of truth and invalidate stale cache', '{ "price": 1399, "version": 8 }', '200 { "id": "p_42", "version": 9 }'),
  ],
  'CDNs & Edge Delivery': [
    endpoint('asset', 'GET', '/assets/:version/:file', 'Serve immutable content from the nearest edge', 'headers: { "Accept-Encoding": "br" }', '200 binary · cache-control: public,max-age=31536000'),
    endpoint('manifest', 'GET', '/v1/videos/:id/manifest', 'Return an adaptive streaming manifest', 'query: { quality: "auto" }', '200 { "segments": ["360p", "720p", "1080p"] }'),
  ],
  'SQL vs NoSQL': [
    endpoint('order', 'POST', '/v1/orders', 'Use a transaction for strongly related writes', '{ "userId": "u_4", "items": [{ "sku": "s_9", "qty": 1 }] }', '201 { "orderId": "o_71", "state": "confirmed" }'),
    endpoint('catalog', 'GET', '/v1/products/:id', 'Read a flexible product document', 'path: { id: "p_42" }', '200 { "id": "p_42", "attributes": { "color": "black" } }'),
  ],
  'Indexes & Replication': [
    endpoint('activity', 'GET', '/v1/users/:id/activity', 'Use a composite index and cursor pagination', 'query: { cursor: "t_900", limit: 20 }', '200 { "items": [...], "nextCursor": "t_880" }'),
    endpoint('profile', 'PATCH', '/v1/users/:id', 'Write to the primary before replicas catch up', '{ "displayName": "Asha", "version": 12 }', '200 { "version": 13, "replicationLagMs": 18 }'),
  ],
  'Database Sharding': [
    endpoint('user', 'GET', '/v1/users/:id', 'Derive the shard from a stable partition key', 'path: { id: "u_8421" }', '200 { "id": "u_8421", "shard": "shard_02" }'),
    endpoint('update', 'PUT', '/v1/users/:id/settings', 'Keep a user write on one shard', '{ "theme": "dark", "version": 4 }', '200 { "version": 5 }'),
  ],
  'CAP & Consistency': [
    endpoint('prefs-write', 'PUT', '/v1/users/:id/preferences', 'Choose write behavior during a partition', '{ "locale": "en-IN", "version": 6 }', '202 { "accepted": true, "consistency": "eventual" }'),
    endpoint('prefs-read', 'GET', '/v1/users/:id/preferences', 'Expose freshness in a cross-region read', 'headers: { "X-Min-Version": "6" }', '200 { "locale": "en-IN", "version": 6 }'),
  ],
  'Queues & Event Streams': [
    endpoint('process', 'POST', '/v1/uploads/:id/process', 'Accept work without blocking on processing', '{ "profile": "1080p", "idempotencyKey": "job_77" }', '202 { "jobId": "job_77", "statusUrl": "/v1/jobs/job_77" }'),
    endpoint('job', 'GET', '/v1/jobs/:id', 'Poll durable asynchronous job state', 'path: { id: "job_77" }', '200 { "status": "running", "progress": 62 }'),
  ],
  'Rate Limiting': [
    endpoint('limited', 'ANY', '/v1/*', 'Apply an identity-aware token bucket', 'headers: { "Authorization": "Bearer ..." }', '200 response · or 429 with Retry-After: 12'),
    endpoint('status', 'GET', '/v1/rate-limit/status', 'Make the current quota visible to a client', 'no request body', '200 { "limit": 100, "remaining": 37, "resetAt": "..." }'),
  ],
  'Consistent Hashing': [
    endpoint('cache-read', 'GET', '/cache/:key', 'Map the key clockwise to one cache node', 'path: { key: "profile:u_42" }', '200 { "value": "...", "node": "cache-b" }'),
    endpoint('cache-write', 'PUT', '/cache/:key', 'Store a key with limited remapping', '{ "value": "...", "ttlSeconds": 300 }', '204 no content'),
  ],
  'Design a URL Shortener': [
    endpoint('create', 'POST', '/v1/links', 'Create a unique short link', '{ "longUrl": "https://example.com/interview", "customAlias": null }', '201 { "shortCode": "aZ91k", "shortUrl": "https://sho.rt/aZ91k" }'),
    endpoint('redirect', 'GET', '/:shortCode', 'Resolve and redirect on the latency-critical path', 'path: { shortCode: "aZ91k" }', '302 location: https://example.com/interview'),
    endpoint('stats', 'GET', '/v1/links/:code/stats', 'Read asynchronously aggregated analytics', 'query: { window: "7d" }', '200 { "clicks": 1842, "countries": { "IN": 712 } }'),
  ],
  'Design Real-time Chat': [
    endpoint('socket', 'WS', '/v1/realtime', 'Maintain a bidirectional authenticated session', '{ "type": "auth", "token": "..." }', '{ "type": "ready", "connectionId": "ws_42" }'),
    endpoint('message', 'POST', '/v1/conversations/:id/messages', 'Persist and fan out one message', '{ "clientMessageId": "m_local_9", "text": "Hello" }', '202 { "messageId": "m_901", "sequence": 441 }'),
    endpoint('history', 'GET', '/v1/conversations/:id/messages', 'Page backward through stable message history', 'query: { before: 441, limit: 30 }', '200 { "messages": [...], "nextBefore": 411 }'),
  ],
  'Design a News Feed': [
    endpoint('feed', 'GET', '/v1/feed', 'Return a ranked, cursor-based home feed', 'query: { cursor: "rank_87", limit: 20 }', '200 { "items": [...], "nextCursor": "rank_66" }'),
    endpoint('post', 'POST', '/v1/posts', 'Create a post and start fan-out', '{ "text": "My interview notes", "mediaIds": [] }', '201 { "postId": "p_902", "fanout": "queued" }'),
  ],
  'Design Video Streaming': [
    endpoint('upload', 'POST', '/v1/uploads', 'Create a resumable upload session', '{ "fileName": "lesson.mp4", "sizeBytes": 91827364 }', '201 { "uploadId": "up_72", "parts": 18 }'),
    endpoint('play', 'GET', '/v1/videos/:id/playback', 'Authorize playback and choose a CDN manifest', 'query: { device: "mobile", network: "4g" }', '200 { "manifestUrl": "https://cdn.../master.m3u8" }'),
  ],
  'Design Ride Hailing': [
    endpoint('location', 'PUT', '/v1/drivers/:id/location', 'Stream a driver location with bounded staleness', '{ "lat": 19.076, "lng": 72.8777, "capturedAt": 1724300000 }', '204 no content'),
    endpoint('ride', 'POST', '/v1/rides', 'Create an idempotent ride request', '{ "pickup": [19.076,72.8777], "dropoff": [19.11,72.91], "idempotencyKey": "ride_9" }', '202 { "rideId": "r_71", "status": "matching" }'),
    endpoint('accept', 'POST', '/v1/rides/:id/accept', 'Atomically let only one driver win', '{ "driverId": "d_42", "offerVersion": 3 }', '200 { "status": "matched", "driverId": "d_42" }'),
  ],
  'Design Notifications': [
    endpoint('send', 'POST', '/v1/notifications', 'Accept a durable multi-channel notification', '{ "userId": "u_42", "template": "offer_ready", "channels": ["push","email"] }', '202 { "notificationId": "n_91", "status": "queued" }'),
    endpoint('status', 'GET', '/v1/notifications/:id', 'Expose per-channel delivery state', 'path: { id: "n_91" }', '200 { "push": "delivered", "email": "sent" }'),
    endpoint('prefs', 'PATCH', '/v1/users/:id/preferences', 'Honor channel and quiet-hour preferences', '{ "push": true, "email": false, "quietHours": "22:00-07:00" }', '200 { "version": 8 }'),
  ],
};

function nodeKind(label: string): DiagramNode['kind'] {
  const value = label.toLowerCase();
  if (/client|user|browser|rider|driver|creator|viewer|producer|reader/.test(value)) return 'client';
  if (/database|store|sql|document|replica|primary|shard|counter|index/.test(value)) return 'database';
  if (/cache|cdn|edge|pop|object/.test(value)) return value.includes('cache') ? 'cache' : 'edge';
  if (/queue|broker|stream|dead letter/.test(value)) return 'queue';
  if (/gateway|api|router|load balancer|dns|limiter/.test(value)) return 'gateway';
  if (/partition|region|hash ring|network/.test(value)) return 'network';
  return 'service';
}

const purposeByKind: Record<DiagramNode['kind'], string> = {
  client: 'Initiates the operation and owns the user-visible latency.',
  gateway: 'Authenticates, validates, routes, and protects the downstream path.',
  service: 'Applies domain rules and coordinates state changes.',
  cache: 'Serves hot data quickly while managing staleness.',
  queue: 'Buffers spikes and makes asynchronous work durable.',
  database: 'Persists the source of truth and answers indexed queries.',
  edge: 'Moves static or large content close to the user.',
  network: 'Controls placement, partitioning, or cross-region behavior.',
};

const failureByKind: Record<DiagramNode['kind'], string> = {
  client: 'Retries can duplicate writes; use an idempotency key.',
  gateway: 'A gateway outage blocks all traffic; run multiple instances across zones.',
  service: 'Slow dependencies consume workers; use deadlines, circuit breakers, and bulkheads.',
  cache: 'Miss storms can overload storage; add request coalescing and TTL jitter.',
  queue: 'Consumers may process twice; make handlers idempotent and monitor lag.',
  database: 'Hot keys or replica lag can break latency and freshness expectations.',
  edge: 'Stale objects can outlive a deploy; version assets and define invalidation.',
  network: 'Partitions force an availability-versus-consistency decision.',
};

export function buildDiagram(title: string, labels: string[]): LessonDay['diagram'] {
  const endpoints = endpointCatalog[title] ?? genericEndpoints(title);
  const nodes: DiagramNode[] = labels.map((label, index) => {
    const kind = nodeKind(label);
    return {
      id: `n${index}`,
      label,
      kind,
      detail: kind === 'client' ? 'Request origin' : kind === 'database' ? 'Durable state' : purposeByKind[kind].split('.')[0],
      purpose: purposeByKind[kind],
      failure: failureByKind[kind],
      x: labels.length > 5 && index === labels.length - 1 ? 76 : 10 + (index % 5) * 20,
      y: labels.length > 5 && index === labels.length - 1 ? 78 : 28 + (index % 2) * 25,
    };
  });

  return {
    nodes,
    edges: nodes.slice(1).map((node, index) => [nodes[index].id, node.id]),
    endpoints,
    steps: nodes.map((node, index) => {
      const activeEndpoint = endpoints[index % endpoints.length];
      return {
        title: `${node.label} handles the packet`,
        endpointId: activeEndpoint.id,
        packet: `${activeEndpoint.method} ${activeEndpoint.path}`,
        narration: index === 0
          ? `${node.label} starts ${activeEndpoint.method} ${activeEndpoint.path}. The request now enters the latency budget.`
          : `${node.label} ${node.purpose.charAt(0).toLowerCase()}${node.purpose.slice(1)} The request advances only after this responsibility succeeds.`,
        remember: `${node.label}: ${node.failure}`,
      };
    }),
  };
}
